import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./inquiryDocument.module.css";
import i18n from "../../i18n";
import { getPrintDate, getTimeFromTimestamp } from "../../utils/utils";
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  useTakeInquiryMutation,
  usePostInquiryUpdateMutation,
  useGetMainPageInfoQuery,
  useTakeAllInquiriesOfTypeMutation,
  useLazyGetUserByGuidQuery,
} from "../../services/api/user.api";
import { TUsersWithOutTherapist, TTakenByOthers } from "../../services/types/auth.types";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import Modal from "../modals/modal/Modal";
import personIcon from "../../images/person_icon.svg";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import { noDataLabel } from "../../utils/constants";
import useAutosizeTextArea from "../../hooks/useAutosizeTextarea";
import StatusButton from "../inquiryStatusButton/statusButton";
import ChangeStatusConfirmationPopup from "../modals/changeStatusConfirmationPopup/ChangeStatusConfirmationPopup";
import PrivacyProtectionPopup from "../modals/privacyProtectionPopup/privacyProtectionPopup";
import { useNavigate } from "react-router-dom";
import { wrapUserForReport } from "../../utils/user";
import ReturnInquiryConfirmationPopup from "../modals/returnInquiryConfirmation/returnInquiryConfirmationPopup";

type TInquiryDoc = TUsersWithOutTherapist | TTakenByOthers;

type TParsedComment = {
  Id: number;
  Comment: string;
  TherapistName: string;
  CreateOn: string;
};

interface IInquiryDocumentProps {
  inquiry: TInquiryDoc;
  openDocument: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

function parseComments(comments: string | null): TParsedComment[] {
  if (!comments) return [];
  try {
    const parsed = JSON.parse(comments);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function InquiryDocument({ inquiry, openDocument, onClose, readOnly = false }: IInquiryDocumentProps) {
  const navigate = useNavigate();
  const [localInquiry, setLocalInquiry] = useState<TInquiryDoc>(inquiry);
  const [updates, setUpdates] = useState<TParsedComment[]>([]);
  const [textAreaValue, setTextAreaValue] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [isConfirmPopupOpen, setConfirmPopupOpen] = useState(false);
  const [openPrivacyPopup, setOpenPrivacyPopup] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const admin = useAppSelector(adminSelector);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [takeInquiryServer, { isLoading: isLoadingTake }] = useTakeInquiryMutation();
  const [postInquiryUpdate] = usePostInquiryUpdateMutation();
  const { refetch: refetchMainPageInfo } = useGetMainPageInfoQuery(undefined, {
    skip: !admin?.companyId,
  });
  const [takeAllInquiries] = useTakeAllInquiriesOfTypeMutation();
  const [getUserByGuid] = useLazyGetUserByGuidQuery();

  const isTaken = localInquiry.therapistsId && localInquiry.therapistsId > 0;

  useAutosizeTextArea(textAreaRef.current, textAreaValue);

  useEffect(() => {
    setLocalInquiry(inquiry);
    setUpdates(parseComments(inquiry.comments));
    setTextAreaValue("");
  }, [inquiry]);

  const refetchAndSync = useCallback(() => {
    refetchMainPageInfo().then(({ data }) => {
      if (!data) return;
      const updated = [
        ...data.myInquiries,
        ...data.usersWithOutTherapist,
        ...data.takenByOthers,
      ].find((i) => i.num === localInquiry.num);
      if (updated) {
        setLocalInquiry(updated);
        setUpdates(parseComments(updated.comments));
      }
    });
  }, [refetchMainPageInfo, localInquiry.num]);

  const handleMutationResponse = useCallback(
    (res: any, onSuccess?: () => void) => {
      if ("error" in res && res?.error?.originalStatus !== 200) {
        setOpenModal(true);
      } else {
        onSuccess?.();
        setOpenSuccessModal(true);
      }
      refetchAndSync();
    },
    [refetchAndSync]
  );

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTextAreaValue(event.target.value);
  };

  const handleTakeInquiry = (e: React.MouseEvent) => {
    e.stopPropagation();
    takeInquiryServer({
      Id: 0,
      Cycle: localInquiry.cycle,
      UsersGuid: localInquiry.guid,
      TherapistsGuid: admin.guid ?? "",
      QuestionnaireId: localInquiry.userQuestionnaireId,
      Status: localInquiry.status || 1,
      QuestionnaireCompaniesId: localInquiry.questionnaireCompaniesId,
    }).then((res) => handleMutationResponse(res));
  };

  const handleSendUpdate = () => {
    if (!textAreaValue.trim()) return;
    const creationDate = new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000
    ).toISOString();

    postInquiryUpdate({
      Id: "0",
      Cycle: localInquiry.cycle,
      UsersGuid: localInquiry.guid.toString(),
      TherapistsGuid: admin.guid ?? "",
      QuestionnaireId: localInquiry.userQuestionnaireId,
      Comment: textAreaValue,
      CreateOn: creationDate,
      QuestionnaireCompaniesId: localInquiry.questionnaireCompaniesId,
    }).then((res) => {
      if ("data" in res) {
        setTextAreaValue("");
        handleMutationResponse(res);
      } else {
        setOpenErrorModal(true);
      }
    });
  };

  const displayName = (doc: TInquiryDoc) =>
    doc.fullName?.trim() ||
    [doc.firstName, doc.lastName].filter(Boolean).join(" ").trim() ||
    doc.usersName ||
    t(noDataLabel);

  const onStatusChange = (newStatusId: number) => {
    setSelectedStatusId(newStatusId);
    setConfirmPopupOpen(true);
  };
  const handleConfirmSingle = () => {
    if (selectedStatusId === null) return;

    takeInquiryServer({
      Id: localInquiry.therapistsUsersId ?? 0,
      Cycle: localInquiry.cycle,
      UsersGuid: localInquiry.guid,
      TherapistsGuid: admin.guid,
      QuestionnaireId: localInquiry.userQuestionnaireId,
      Status: selectedStatusId,
      QuestionnaireCompaniesId: localInquiry.questionnaireCompaniesId,
    }).then((res) =>
      handleMutationResponse(res, () => {
        setConfirmPopupOpen(false);
        setSelectedStatusId(null);
      })
    );
  };

  const handleConfirmAll = () => {
    if (selectedStatusId === null) return;

    takeAllInquiries({
      UsersGuid: localInquiry.guid,
      TherapistsGuid: admin.guid,
      QuestionnaireId: localInquiry.userQuestionnaireId,
      Status: selectedStatusId,
      QuestionnaireCompaniesId: localInquiry.questionnaireCompaniesId,
    }).then((res) =>
      handleMutationResponse(res, () => {
        setConfirmPopupOpen(false);
        setSelectedStatusId(null);
      })
    );
  };

  const handlePrivacyPopupClose = () => {
    const guid = localInquiry?.guid || "0";

    if (guid === "0") {
      setOpenErrorModal(true);
      return;
    } else {
      getUserByGuid(guid).unwrap().then((res) => {
        if (res) {
          const wrappedUser = wrapUserForReport(res, guid);
          navigate("/users/edit", { state: { user: wrappedUser } });
        } else {
          setOpenErrorModal(true);
        }
      });
    }
    setOpenPrivacyPopup(false);
  };

  const returnInquiry = () => {
    takeInquiryServer({
      Id: localInquiry.num,
      Cycle: localInquiry.cycle,
      UsersGuid: localInquiry.guid,
      TherapistsGuid: localInquiry.guid,
      QuestionnaireId: localInquiry.userQuestionnaireId,
      Status: -1,
      QuestionnaireCompaniesId: localInquiry.questionnaireCompaniesId,
    }).then((res) =>
      handleMutationResponse(res, () => setOpenConfirmationModal(false))
    );
  };

  return (
    <article
      dir={i18n.dir()}
      className={`${styles.document} ${openDocument ? styles.document_visible : ""}`}
    >
      <button className={styles.documentCloseBtn} onClick={onClose} />

      <h4 className={styles.documentTitle}>
        {t("DOCUMENT_TITLE")} {localInquiry?.num}
      </h4>

      <div className={styles.documentInfo}>
        <p className={styles.documentLabel}>
          {t("DOCUMENT_LABEL_SUBJECT")}
          <span className={styles.documentText}>
            {localInquiry?.questionnaireCompaniesType || t(noDataLabel)}
          </span>
        </p>
        <p className={styles.documentLabel}>
          {t("DOCUMENT_LABEL_OPEN_DATE")}
          <span className={styles.documentText}>
            {localInquiry?.timestamp ? getPrintDate(new Date(localInquiry.timestamp)) : t(noDataLabel)}
          </span>
        </p>
        <p className={styles.documentLabel}>
          {t("DOCUMENT_LABEL_CYCLE")}
          <span className={styles.documentText}>{localInquiry?.cycle}</span>
        </p>
        <p className={styles.documentLabel}>
          {t("DOCUMENT_LABEL_OPEN_TIME")}
          <span className={styles.documentText}>
            {localInquiry?.timestamp ? getTimeFromTimestamp(localInquiry.timestamp) : t(noDataLabel)}
          </span>
        </p>
      </div>

      <div className={styles.documentStatusLabel}>
        {t("DOCUMENT_LABEL_STATUS")}
        <StatusButton
          document={localInquiry}
          onStatusChange={onStatusChange}
          updatable={!readOnly && localInquiry.therapistsId === admin.id}
        />
      </div>

      <div className={styles.documentLinks}>
        <button
          className={`button button_white ${styles.btn} ${styles.btn_type_actionReport}`}

          type="button"
          onClick={() => setOpenPrivacyPopup(true)}
        >
          <img src={personIcon} alt={t('PTEEK_TIK')} className={styles.view_icon} />
          {t('PTEEK_TIK')}
        </button>        {/* <button
          className={`button button_white ${styles.btn} ${styles.btn_type_actionReport} ${styles.btn_disabled}`}
          disabled
        >
          <span className={styles.btn__label}>{t("QUESTION_REPORTS_ACTION_REPORT_BUTTON")}</span>
        </button> */}
      </div>

      <h5 className={styles.documentSubTitle}>{t("DOCUMENT_SUBTITLE_CONTACT")}</h5>
      <p className={styles.documentLabel}>
        {t("DOCUMENT_LABEL_USER_NUMBER")}
        <span className={styles.documentText}>{localInquiry?.usersId}</span>
      </p>
      <p className={styles.documentLabel}>
        {t("DOCUMENT_LABEL_USER_NAME")}
        <span className={styles.documentText}>{displayName(localInquiry)}</span>
      </p>

      <h5 className={styles.documentSubTitle}>{t("ADD_TREATMENT_PLAN_LABEL_COMMENTS")}</h5>
      {updates.length > 0 ? (
        <ul className={styles.documentUpdates}>
          {updates
            .sort((a, b) => b.Id - a.Id)
            .map((upd) => (
              <li key={upd.Id}>
                <div className={styles.updateInfo}>
                  {/* <p className={styles.documentLabel}>
                    {t("TREATMENT_DOCUMENT_UPDATE_LABEL")}
                    <span className={styles.documentText}>{upd.Id}</span>
                  </p> */}
                  <p className={styles.documentLabel}>
                    {t("DATE")}
                    <span className={styles.documentText}>
                      {upd.CreateOn
                        ? `${getPrintDate(new Date(upd.CreateOn))} ${getTimeFromTimestamp(new Date(upd.CreateOn))}`
                        : t(noDataLabel)}
                    </span>
                  </p>
                </div>
                <p className={styles.documentContent}>{upd.Comment}</p>
                <p className={styles.documentLabel}>
                  {t("DOCUMENT_LABEL_UPDATED_BY")} {upd.TherapistName}
                </p>
              </li>
            ))}
        </ul>
      ) : (
        <p className={styles.noDocuments}>{t("DOCUMENT_NO_UPDATES")}</p>
      )}

      <div className={styles.textAreaContainer}>
        <textarea
          ref={textAreaRef}
          rows={1}
          name="updates"
          placeholder={t("DOCUMENT_TEXTAREA_PLACEHOLDER")}
          className={styles.documentTextArea}
          onChange={handleChange}
          value={textAreaValue}
          disabled={readOnly}
        />
        <button
          className={styles.sendUpdate}
          disabled={readOnly || !textAreaValue.trim()}
          onClick={handleSendUpdate}
        />
      </div>

      {!readOnly && (!isTaken ? (
        <button
          disabled={isLoadingTake}
          className={`button button_white ${styles.btn} hover`}
          onClick={handleTakeInquiry}
        >
          {t("DOCUMENT_BUTTON_TAKE")}
        </button>
      ) : localInquiry.therapistsId === admin.id ? (
        <button
          className={`button button_white ${styles.btn} ${styles.btn_red} hover`}
          onClick={() => setOpenConfirmationModal(true)}
        >
          {t("DOCUMENT_BUTTON_CANCEL_TAKING_INQUIRY")}
        </button>
      ) : null)}

      {openModal && (
        <Modal onClose={() => setOpenModal(false)}>
          <ErrorPopup onClose={() => setOpenModal(false)} />
        </Modal>
      )}
      {openSuccessModal && (
        <Modal onClose={() => setOpenSuccessModal(false)}>
          <GeneralSuccessPopup onClose={() => { setOpenSuccessModal(false); }} />
        </Modal>
      )}
      {openErrorModal && (
        <Modal onClose={() => setOpenErrorModal(false)}>
          <ErrorPopup onClose={() => setOpenErrorModal(false)} />
        </Modal>
      )}
      {openPrivacyPopup && (
        <Modal onClose={() => { setOpenPrivacyPopup(false); }}>
          <PrivacyProtectionPopup
            onClose={handlePrivacyPopupClose}
            clientUsersGuid={inquiry?.guid}
          />
        </Modal>
      )}
      {isConfirmPopupOpen && (
        <Modal onClose={() => setConfirmPopupOpen(false)}>
          <ChangeStatusConfirmationPopup
            onClose={() => setConfirmPopupOpen(false)}
            onConfirmSingle={handleConfirmSingle}
            onConfirmAll={handleConfirmAll}
          />
        </Modal>
      )}
      {openConfirmationModal && (
        <Modal
          onClose={() => {
            setOpenConfirmationModal(false);
          }}
        >
          <ReturnInquiryConfirmationPopup onClose={() => returnInquiry()} />
        </Modal>
      )}
    </article>
  );
}

export default InquiryDocument;
